import { useMutation, useQuery, useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import * as API from '../../api/index.js';
import type { AsyncUploadState } from '../../api/types.js';
import { useEnhancedMutation } from '../../hooks/index.js';
import { createDeferredPromise, DeferredPromise } from '../../utils/promise.js';
import { QueryKeys, useTypedQueryClient } from '../queryClient.js';
import {
  useDataFilePreview,
  useDataFilePreviewQueryOptions,
} from './preview.js';

export const useAwsFileUploadData = (
  dataFileId: string,
  options?: { throwOnError?: boolean },
) => {
  return useQuery({
    queryKey: QueryKeys.dataFile.steps.upload.awsUploadData(dataFileId),
    queryFn: () => API.getS3UploadCredentials(dataFileId),
    refetchOnMount: false,
    ...options,
  });
};

export const useDataFileSheetNames = (dataFileId: string) => {
  const queryOptions =
    DataIngestion.DataFile.listDataFileSheetNamesQueryOptions({
      urlParams: { dataFileId },
    });

  return useQuery({ ...queryOptions, gcTime: 0 });
};

export const useUploadDeferredPromise = (dataFileId: string) => {
  const queryClient = useTypedQueryClient();
  const deferredQueryKey = QueryKeys.dataFile.steps.upload.deferred(dataFileId);

  const getDeferred = () => {
    const deffered = queryClient.getQueryData(deferredQueryKey);
    if (!deffered) {
      return createAndSetDeferred();
    }
    return deffered;
  };

  const setDeferred = (deferred: DeferredPromise | null) => {
    queryClient.setQueryData(deferredQueryKey, deferred);
  };

  const createAndSetDeferred = () => {
    const deferred = createDeferredPromise();
    deferred.promise
      // Rejections are surfaced to consumers awaiting getDeferred().promise;
      // this side chain only clears state and must not trigger an
      // unhandled-rejection error when the upload fails.
      .catch(() => undefined)
      .finally(() => {
        setDeferred(null);
      });

    setDeferred(deferred);
    return deferred;
  };

  return {
    getDeferred,
    setDeferred,
    createAndSetDeferred,
  };
};

export const useUploadFile = (dataFileId: string) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useMutation({
    mutationFn: API.uploadFileToS3,
    onMutate: ({ payload }) => {
      const previousPreview = queryClient.getQueryData(previewQueryKey);

      if (previousPreview) {
        const fileName = (payload.get('file') as File).name;

        queryClient.setQueryData(previewQueryKey, {
          ...previousPreview,
          name: fileName,
        });
      }
      return { previousPreview };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(previewQueryKey, context?.previousPreview);
    },
  });
};

export const useParseFile = (dataFileId: string) => {
  const { refetch: refetchDataFilePreview } = useDataFilePreview(dataFileId);
  const { getDeferred } = useUploadDeferredPromise(dataFileId);

  return useMutation({
    mutationFn: async (payload: { fileName: string; resetFile: boolean }) => {
      await API.startParsingFile({
        dataFileId,
        payload,
      });
      await getDeferred().promise;
      return refetchDataFilePreview();
    },
  });
};

export const useUploadFileMutation = (dataFileId: string) => {
  const queryClient = useQueryClient();

  const { mutateAsync: uploadFile } = useUploadFile(dataFileId);
  const { mutateAsync: parseFile } = useParseFile(dataFileId);

  return useMutation({
    mutationFn: async (args: Parameters<typeof uploadFile>[0]) => {
      const fileName = (args.payload.get('file') as File).name;
      await uploadFile(args);
      const { data: dataFilePreview } = await parseFile({
        fileName,
        resetFile: true,
      });
      return dataFilePreview;
    },
    onSuccess: async (dataFilePreview) => {
      if (!dataFilePreview) {
        return;
      }

      queryClient.removeQueries({
        queryKey:
          QueryKeys.dataFile.steps.validation.totalErrorsCount(dataFileId),
      });
      queryClient.removeQueries({
        queryKey:
          QueryKeys.dataFile.steps.channel_assignment.totalErrorsCount(
            dataFileId,
          ),
      });
      queryClient.removeQueries({
        predicate(query) {
          return (
            query.queryKey.includes(dataFileId) &&
            !query.queryKey.includes('preview')
          );
        },
        exact: false,
      });
    },
  });
};

export const useUploadState = (dataFileId: string) => {
  const queryClient = useQueryClient();
  const queryKey = QueryKeys.dataFile.steps.upload.state(dataFileId);

  return useQuery({
    queryKey,
    queryFn: () => queryClient.getQueryData<AsyncUploadState>(queryKey),
    enabled: false,
  });
};

export const useKeepTemplate = (dataFileId: string, discardFile: boolean) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useMutation({
    mutationFn: discardFile ? () => API.discardFile(dataFileId) : undefined,

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: previewQueryKey });

      const previousValue = queryClient.getQueryData(previewQueryKey);

      if (previousValue) {
        queryClient.setQueryData(previewQueryKey, {
          ...previousValue,
          templateHeadersMismatched: false,
          status: 'uncompleted',
          isFileUploaded: false,
        });
      }

      return { previousValue };
    },
  });
};

export const useDiscardTemplate = (dataFileId: string) => {
  const queryClient = useQueryClient();

  const { queryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useEnhancedMutation({
    mutationFn: () => API.discardTemplate(dataFileId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });

      const previousValue = queryClient.getQueryData(queryKey);

      if (previousValue) {
        queryClient.setQueryData(queryKey, {
          ...previousValue,
          fromTemplate: null,
          templateHeadersMismatched: false,
        });
      }

      return { previousValue };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(queryKey, context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to discard template'),
      }),
    },
  });
};

export const useSetDataFileSheetName = (dataFileId: string) => {
  return useEnhancedMutation({
    mutationFn: (payload: DataIngestion.DataFile.SetDataFileSheetNamePayload) =>
      DataIngestion.DataFile.setDataFileSheetName({
        urlParams: { dataFileId },
        data: payload,
      }),
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to select sheet'),
      }),
    },
  });
};
